import { z } from "zod";

// Organization pattern enum - how the ZIP files are organized
export const ZipOrganizationPatternSchema = z.enum([
  "sample_based",
  "doc_type_based",
  "flat",
  "unknown",
]);
export type ZipOrganizationPattern = z.infer<
  typeof ZipOrganizationPatternSchema
>;

// Recursive file tree node structure
export const DetectedFileNodeSchema: z.ZodType<DetectedFileNode> = z.lazy(() =>
  z.object({
    path: z.string(),
    name: z.string(),
    isDirectory: z.boolean(),
    sizeBytes: z.number().nullable(),
    fileType: z.string().nullable(),
    contentPreview: z.string().nullable(),
    children: z.array(DetectedFileNodeSchema),
  }),
);
export type DetectedFileNode = {
  path: string;
  name: string;
  isDirectory: boolean;
  sizeBytes: number | null;
  fileType: string | null;
  contentPreview: string | null;
  children: DetectedFileNode[];
};

// Sample mapping suggestion from LLM
export const SampleFolderMappingSchema = z.object({
  folderPath: z.string(),
  suggestedSourceRowId: z.string().nullable(),
  suggestedSampleIdentifier: z.string().nullable(),
  confidence: z.number(),
});
export type SampleFolderMapping = z.infer<typeof SampleFolderMappingSchema>;

// Document type mapping suggestion
export const DocTypeFolderMappingSchema = z.object({
  folderPath: z.string(),
  suggestedProcedureDocumentId: z.string().nullable(),
  suggestedDocTypeName: z.string().nullable(),
  confidence: z.number(),
});
export type DocTypeFolderMapping = z.infer<typeof DocTypeFolderMappingSchema>;

// Main preview response from parse endpoint
export const EvidenceUploadPreviewSchema = z.object({
  uploadId: z.string(),
  zipFilename: z.string(),
  totalFileCount: z.number(),
  totalSizeBytes: z.number(),
  fileTree: DetectedFileNodeSchema,
  detectedPattern: ZipOrganizationPatternSchema,
  patternConfidence: z.number(),
  patternReasoning: z.string(),
  sampleMappings: z.array(SampleFolderMappingSchema),
  docTypeMappings: z.array(DocTypeFolderMappingSchema),
  // Optional fields for multi-procedure-session uploads
  suggestedProcedureSessionId: z.string().nullable().default(null),
  procedureSessionConfidence: z.number().default(0),
  procedureSessionReasoning: z.string().default(""),
});
export type EvidenceUploadPreview = z.infer<typeof EvidenceUploadPreviewSchema>;

// Lifecycle of a parse run, mirroring the backend's GenerationState. Parsing a
// zip runs on a worker rather than inside the request that uploaded it, so the
// client is handed a state to poll instead of a finished preview.
export const EvidenceRunStateSchema = z.enum([
  "queued",
  "running",
  "succeeded",
  "failed",
]);
export type EvidenceRunState = z.infer<typeof EvidenceRunStateSchema>;

// 202 answer from the async parse endpoint: the upload to poll, and the run
// building its preview. Polling is keyed on `uploadId`, which outlives the run
// record; `runId` is carried for diagnostics only.
export const EvidenceParseDispatchSchema = z.object({
  uploadId: z.string(),
  runId: z.string(),
  state: EvidenceRunStateSchema,
});
export type EvidenceParseDispatch = z.infer<typeof EvidenceParseDispatchSchema>;

// One poll of `GET .../evidence/preview/{uploadId}`. A finished parse answers
// with the whole preview plus `state: "succeeded"`; while the run is still in
// flight only the state comes back. Both shapes carry `state`, so it is read on
// its own and the preview parsed from the same body once the state allows it.
export const EvidenceRunStateEnvelopeSchema = z.object({
  state: EvidenceRunStateSchema,
});

// Request body for confirming upload with sample-based mapping
// Note: confirmed mappings require non-null values (unlike suggestions)
export const ConfirmSampleMappingSchema = z.object({
  folder_path: z.string(),
  source_row_id: z.string(),
});
export type ConfirmSampleMapping = z.infer<typeof ConfirmSampleMappingSchema>;

// Request body for confirming upload with doc-type-based mapping
export const ConfirmDocTypeMappingSchema = z.object({
  folder_path: z.string(),
  procedure_document_id: z.string(),
});
export type ConfirmDocTypeMapping = z.infer<typeof ConfirmDocTypeMappingSchema>;

// Response from confirm endpoint
export const EvidenceUploadResultSchema = z.object({
  createdDocumentCount: z.number(),
  matchedDocumentCount: z.number(),
  skippedDocumentCount: z.number(),
  // Folders whose sample mapping the backend refused, because the sample
  // belongs to a procedure this upload does not write to. Their files were
  // still uploaded and are matched on their contents instead.
  //
  // Defaulted rather than required: it arrives with a backend that has the
  // field, and this bundle can be served against one that does not.
  droppedMappingFolders: z.array(z.string()).default([]),
  status: z.string(),
});
export type EvidenceUploadResult = z.infer<typeof EvidenceUploadResultSchema>;

// 202 answer from the async confirm endpoint. Persisting and dispatching every
// file in the archive happens on a worker, so this arrives long before the
// documents exist.
export const EvidenceConfirmDispatchSchema = z.object({
  uploadId: z.string(),
  runId: z.string(),
  state: EvidenceRunStateSchema,
});
export type EvidenceConfirmDispatch = z.infer<
  typeof EvidenceConfirmDispatchSchema
>;

// One poll of `GET .../evidence/confirm/{uploadId}/status`. The counts arrive
// only once the run has succeeded.
export const EvidenceConfirmStatusSchema = z.object({
  uploadId: z.string(),
  runId: z.string(),
  state: EvidenceRunStateSchema,
  result: EvidenceUploadResultSchema.nullable().default(null),
});
export type EvidenceConfirmStatus = z.infer<typeof EvidenceConfirmStatusSchema>;
